import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useEffect } from 'react';
import { ControlledDropdown } from '../components/controlled-dropdown/ControlledDropdown';
import {
  winnerCommunicationModules,
  type WinnerCommunicationModule,
} from './winner-communications';

export type SelectWinnerCommunicationFormType = Static<typeof SelectWinnerCommunicationFormSchema>;
export const SelectWinnerCommunicationFormSchema = Type.Object(
  {
    winnerCommunicationModule: Type.Union(
      winnerCommunicationModules.map((module) => Type.Literal(module))
    ),
  },
  { additionalProperties: true, $id: 'SelectWinnerCommunicationFormSchema' }
);

export type SelectWinnerCommunicationProps = {
  retailUnit: string;
  form: UseFormReturn<SelectWinnerCommunicationFormType>;
  onValidation: (moduleType: string, isValid: boolean) => void;
};

export const SelectWinnerCommunication: React.FC<SelectWinnerCommunicationProps> = ({
  retailUnit,
  form,
  onValidation,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const isValid = form.formState.isValid;
    onValidation('winner-communication-module', isValid);
  }, [form.formState, onValidation]);

  const {
    data: retailUnitData,
    isLoading: retailUnitIsLoading,
    error: retailUnitError,
  } = useGetRetailUnitQuery({ retailUnit });

  const options: Array<{ translationKey: string; value: WinnerCommunicationModule }> = [
    // eslint-disable-next-line i18next/no-literal-string
    { translationKey: 'winner-communication-no-communication-label', value: 'no-communication' },
  ];

  if (retailUnitData && !retailUnitIsLoading && !retailUnitError) {
    retailUnitData.retailUnit.winnerCommunication?.forEach((winnerCommunication) => {
      options.push({
        translationKey: `winner-communication-${winnerCommunication}-label`,
        value: winnerCommunication,
      });
    });
  }

  return (
    <div>
      <form>
        <ControlledDropdown
          id="select-winner-communication-module"
          control={form.control}
          name="winnerCommunicationModule"
          label={t('lottery-form-winner-communication-module-label')}
          options={options.map(({ translationKey, value }) => ({
            name: t(translationKey),
            value,
          }))}
          errorMessage={undefined}
          hintText={t('lottery-form-winner-communication-module-placeholder')}
        />
      </form>
    </div>
  );
};
